// ============================================================
// TMR Portal Admin — Contratos de paciente (rota /contratos-paciente)
// ============================================================
import { useState } from 'react';
import { FilterTabs, PageHeader, SearchBox, Table, Td } from '../../components/ui';
import { Btn } from '../../components/primitives';
import { Pagination } from '../../components/Pagination';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { useDebounced } from '../../useDebounced';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import {
  PATIENT_CONTRACT_PAGE_SIZE,
  patientContractExportPage,
  usePatientContracts,
} from '../../data/patientContracts/queries';
import { ExportMenu } from '../../components/ExportMenu';
import { PATIENT_CONTRACT_EXPORT_COLUMNS } from './exportColumns';
import {
  PATIENT_CONTRACT_STATUS_OPTIONS,
  contractStatusLabel,
  formatPatientDocument,
  summaryTiles,
} from '../../data/patientContracts/status';
import type { PatientContractRow, PatientContractStatus } from '../../data/patientContracts/contract';
import { PatientContractDetailPanel } from './PatientContractDetailPanel';

/** The status filter: every status, or one of them. */
type StatusFilter = 'all' | PatientContractStatus;

const TABS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  ...PATIENT_CONTRACT_STATUS_OPTIONS.map((option) => ({ value: option.value as StatusFilter, label: option.label })),
];

/**
 * The contracts patients signed, newest first.
 *
 * **These are the v1.1 engine's records, shown here.** It issues them, the patient signs into them, and its
 * routine produces the PDF; the V2 reads them and can ask for a failed processing to be tried again
 * (`24` §3.3). Nothing on this screen creates or cancels a contract, and that is a boundary rather than a
 * gap — issuing belongs to the sale, which lives over there.
 *
 * **One search box, three meanings.** Name, CPF or contract number: the operator has one of the three in
 * front of them, usually because a patient or a clinic is on the phone, and should not have to say which.
 * The backend decides — filtering the rows already on screen would claim to have searched everything.
 */
export function PatientContractsPage() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [issuedFrom, setIssuedFrom] = useState('');
  const [issuedTo, setIssuedTo] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const canRead = usePermission(PERMISSIONS.patientContractsRead);

  const filters = {
    status: status === 'all' ? undefined : status,
    search: useDebounced(search, 350).trim() || undefined,
    issuedFrom: issuedFrom || undefined,
    issuedTo: issuedTo || undefined,
    page,
  };

  const list = usePatientContracts(filters, canRead);

  // The route is already gated, so this is the operator who reached it by URL (06 §6).
  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState
            message="Você não tem permissão para ver os contratos de paciente."
            hint="Peça o acesso a quem administra os papéis."
          />
        </div>
      </div>
    );
  }

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / (list.data?.pageSize ?? PATIENT_CONTRACT_PAGE_SIZE)));
  const filtered = Boolean(filters.search || filters.status || filters.issuedFrom || filters.issuedTo);

  /** Back to everything — offered where a filter left the operator looking at nothing. */
  function clearFilters() {
    setStatus('all');
    setSearch('');
    setIssuedFrom('');
    setIssuedTo('');
    setPage(1);
  }

  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      // Any change of filter returns to the first page: page 4 of the previous filter is a different
      // set of rows, and landing on an empty one reads as "nenhum resultado".
      setPage(1);
    };
  }

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Contratos de paciente"
        title="Contratos de paciente"
        desc="Os contratos emitidos pelo sistema de vendas, com as evidências da assinatura. Emitir e cancelar seguem do outro lado."
        actions={
          <ExportMenu
            spec={{
              fileName: 'contratos-paciente',
              columns: PATIENT_CONTRACT_EXPORT_COLUMNS,
              // The screen's own filters, so the file is what is on screen rather than the whole base —
              // an export that silently ignored the filter would be the wrong answer, confidently.
              fetchPage: patientContractExportPage(filters),
            }}
            totalCount={list.data?.total ?? 0}
            disabled={list.isPending || list.isError}
          />
        }
      />

      {/* The whole base, not the filter — the table's own count is under the tabs. Kept on screen while a
          new page loads, because the totals do not change with the filter. */}
      <div className="pa-kpi-grid">
        {summaryTiles(list.data?.totals).map((tile) => (
          <div key={tile.label} className="pa-card" style={{ padding: 18 }}>
            <div className="pa-kpi-label" style={{ marginBottom: 8 }}>{tile.label}</div>
            <div className="pa-num" style={{ fontSize: 26, lineHeight: 1 }}>{tile.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <SearchBox
          value={search}
          onChange={reset(setSearch)}
          placeholder="Nome do paciente, CPF ou número do contrato"
        />

        <DayField label="Emitido de" value={issuedFrom} onChange={reset(setIssuedFrom)} />
        <DayField label="até" value={issuedTo} onChange={reset(setIssuedTo)} />
      </div>

      <FilterTabs tabs={TABS} value={status} onChange={(value) => reset(setStatus)(value as StatusFilter)} />

      {list.isPending ? (
        <Skeleton rows={8} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <div className="pa-card">
          <EmptyState
            message={filtered ? 'Nenhum contrato encontrado com estes filtros.' : 'Nenhum contrato emitido ainda.'}
            hint={
              filtered
                ? 'Tente uma busca mais ampla ou limpe o período.'
                : 'Os contratos aparecem aqui assim que o sistema de vendas emite o primeiro.'
            }
            action={
              filtered ? (
                <Btn variant="secondary" onClick={clearFilters}>
                  Limpar filtros
                </Btn>
              ) : undefined
            }
          />
        </div>
      ) : (
        <>
          <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
            {total === 1 ? '1 contrato' : `${total} contratos`}
          </span>

          <Table
            columns={[
              { label: 'Nº' },
              { label: 'Paciente' },
              { label: 'Clínica' },
              { label: 'Documento' },
              { label: 'Situação' },
              { label: 'Emitido em' },
              { label: 'Assinado em' },
            ]}
          >
            {items.map((item) => (
              <ContractRow key={item.id} contract={item} onOpen={() => setOpenId(item.id)} />
            ))}
          </Table>

          <Pagination page={page} pageCount={pageCount} onChange={setPage} />
        </>
      )}

      {openId && (
        <PatientContractDetailPanel documentId={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

/** One contract's row. */
function ContractRow({ contract, onOpen }: { contract: PatientContractRow; onOpen: () => void }) {
  return (
    <tr onClick={onOpen} style={{ cursor: 'pointer' }}>
      <Td>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{contract.number}</span>
      </Td>
      <Td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 600 }}>{contract.patientName ?? 'Sem signatário registrado'}</span>
          <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
            {formatPatientDocument(contract.patientDocument)}
          </span>
        </div>
      </Td>
      <Td>
        {/* A clinic with no V2 Empresa correlated yet is normal while the base is being backfilled (19).
            The row shows the producer id, which is what somebody would look it up by. */}
        {contract.companyName ?? `Produtor ${contract.legacyProducerId}`}
      </Td>
      <Td>{contract.documentTypeName}</Td>
      <Td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span>{contractStatusLabel(contract.status, contract.hasPdf)}</span>
          {contract.processingFailed && (
            <span style={{ fontSize: 12.5, color: 'oklch(38% 0.12 28)' }}>Processamento falhou</span>
          )}
        </div>
      </Td>
      <Td>{new Date(contract.issuedAt).toLocaleDateString('pt-BR')}</Td>
      <Td>{contract.signedAt ? new Date(contract.signedAt).toLocaleDateString('pt-BR') : '—'}</Td>
    </tr>
  );
}

/** A day filter. Native date input: it is the one control every browser gets right for a single day. */
function DayField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label className="pa-form-label" style={{ marginBottom: 0 }}>
        {label}
      </label>
      <input
        type="date"
        className="pa-input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          padding: '8px 10px',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-sans)',
          fontSize: 14,
        }}
      />
    </div>
  );
}
